import { useEffect } from "react";
import { Calendar } from "primereact/calendar";
import { InputTextarea } from "primereact/inputtextarea";
import { ValidationMessage } from "sj-ui-toolkit/src/components";
import type { TournamentFormState } from "@/api/tournaments";
import type { TournamentValidationErrors } from "@/hooks/useTournamentValidation";

// ─── Date helpers ─────────────────────────────────────────────────────────────

const getTomorrowAt9AM = (): Date => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  date.setHours(9, 0, 0, 0);
  return date;
};

const getDayAfterTomorrowAt5PM = (): Date => {
  const date = new Date();
  date.setDate(date.getDate() + 2);
  date.setHours(17, 0, 0, 0);
  return date;
};

const getTimeZoneLabel = (): string => {
  const date = new Date();
  const parts = new Intl.DateTimeFormat(undefined, {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timeZoneName: "short",
    hour: "numeric",
  }).formatToParts(date);
  return parts.find(p => p.type === "timeZoneName")?.value ?? "";
};

// ─── Component ────────────────────────────────────────────────────────────────

interface TournamentSettingsProps {
  settings: TournamentFormState["settings"];
  onChangeSettings: (settings: TournamentFormState["settings"]) => void;
  showValidationMessages: boolean;
  validationErrors: TournamentValidationErrors["settings"];
  isTournamentInProgress: boolean;
}

export function TournamentSettings({
  settings,
  onChangeSettings,
  showValidationMessages,
  validationErrors,
  isTournamentInProgress,
}: TournamentSettingsProps) {
  const updateField = (
    field: keyof TournamentFormState["settings"],
    value: TournamentFormState["settings"][keyof TournamentFormState["settings"]]
  ) => {
    onChangeSettings({ ...settings, [field]: value });
  };

  // Set default dates for new tournaments
  useEffect(() => {
    const isNewTournament = !settings.startDate && !settings.endDate;
    if (isNewTournament) {
      onChangeSettings({
        ...settings,
        startDate: getTomorrowAt9AM(),
        endDate: getDayAfterTomorrowAt5PM(),
      });
    }
    // Only run once on mount
  }, []);

  const tzLabel = getTimeZoneLabel();

  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-6">
        <div className="flex-1">
          <label className="block mb-2 font-medium" htmlFor="tournament-start-date">
            Start Date & Time
          </label>
          <div className="relative inline-block">
            <Calendar
              inputId="tournament-start-date"
              data-testid="tournament-start-date-input"
              value={settings.startDate ? new Date(settings.startDate as string | Date) : null}
              onChange={e => updateField("startDate", e.value ?? null)}
              showTime
              showSeconds={false}
              hourFormat="12"
              minDate={new Date()}
              disabled={isTournamentInProgress}
              inputClassName="pr-16"
            />
            {tzLabel && (
              <span className="absolute right-9 top-1/2 -translate-y-1/2 text-muted">
                {tzLabel}
              </span>
            )}
          </div>
          {showValidationMessages && validationErrors.startDate.hasError && (
            <ValidationMessage severity="error" text={validationErrors.startDate.message} />
          )}
        </div>
        <div className="flex-1">
          <label className="block mb-2 font-medium" htmlFor="tournament-end-date">
            End Date & Time
          </label>
          <div className="relative inline-block">
            <Calendar
              inputId="tournament-end-date"
              value={settings.endDate ? new Date(settings.endDate as string | Date) : null}
              onChange={e => updateField("endDate", e.value ?? null)}
              showTime
              showSeconds={false}
              hourFormat="12"
              minDate={
                settings.startDate ? new Date(settings.startDate as string | Date) : new Date()
              }
              disabled={isTournamentInProgress}
              inputClassName="pr-16"
            />
            {tzLabel && (
              <span className="absolute right-9 top-1/2 -translate-y-1/2 text-muted">
                {tzLabel}
              </span>
            )}
          </div>
          {showValidationMessages && validationErrors.endDate.hasError && (
            <ValidationMessage severity="error" text={validationErrors.endDate.message} />
          )}
        </div>
      </div>
      <div>
        <label className="block mb-2 font-medium" htmlFor="tournament-contact-info">
          Contact Info
        </label>
        <InputTextarea
          id="tournament-contact-info"
          data-testid="tournament-contact-info-input"
          className="w-full"
          rows={4}
          value={settings.contactPersons}
          onChange={e => updateField("contactPersons", e.target.value)}
          disabled={isTournamentInProgress}
        />
        {showValidationMessages && validationErrors.contactPersons.hasError && (
          <ValidationMessage severity="error" text={validationErrors.contactPersons.message} />
        )}
      </div>
      <div>
        <label className="block mb-2 font-medium mt-4" htmlFor="tournament-prizes">
          Prizes
        </label>
        <InputTextarea
          id="tournament-prizes"
          data-testid="tournament-prizes-input"
          className="w-full"
          rows={4}
          value={settings.prizes}
          onChange={e => updateField("prizes", e.target.value)}
          disabled={isTournamentInProgress}
        />
      </div>
    </div>
  );
}
